// ABOUTME: The storm sky's shared pieces: cloud shapes and placements, the rain veil's fall, and the reduced-motion switch for keyframes.
// ABOUTME: BreathSceneArt, StormHorizon and SkyStrip all draw from these so the clouds and rain are one picture wherever they are placed.

import { keyframes, type Keyframes } from '@emotion/react';
import { CROSSING_STORM_CLOUD_FILL, CROSSING_STORM_SKY_STOPS } from '../../../theme/crossingTokens';

// The reference's design stage, in its own px: every percentage and pixel size in the scene is of this stage, not of the screen.
export const STAGE_W = 850;
export const STAGE_H = 370;
export const SKY_OVERSHOOT = 90; // the sky starts this far above the stage
export const SKY_H = STAGE_H / 2 + SKY_OVERSHOOT; // the sky ends at the stage's 50%, the horizon
// The sky reaches this far above its top so it fills behind the header; at any stage scale of 0.75 or more,
// 800 stage px reaches the scene root's top, which is 600 screen px above the host.
export const SKY_ABOVE_STAGE = 800;
export const SKY_WINDOW_H = SKY_H + SKY_ABOVE_STAGE;
// The storm's gradient is laid out over a box this much wider than the stage on each side, as the prototype's is; the layer itself
// runs as far as the base sky, so a window of any width is covered and the gradient's ends simply continue as flat colour.
const STORM_GRADIENT_SIDE_BLEED = 240;

// The storm gradient written in px from the box's centre (the stage's centre), so its colours sit where they do over a box of
// STAGE_W plus the bleed, however wide the box is. A gradient line at 260 degrees is that box's width and height projected on it.
const STORM_GRADIENT_ANGLE = 260;
const STORM_GRADIENT_LINE_PX = (STAGE_W + 2 * STORM_GRADIENT_SIDE_BLEED) * Math.abs(Math.sin(STORM_GRADIENT_ANGLE * Math.PI / 180))
  + SKY_WINDOW_H * Math.abs(Math.cos(STORM_GRADIENT_ANGLE * Math.PI / 180));
export const STORM_SKY_GRADIENT = `linear-gradient(${STORM_GRADIENT_ANGLE}deg, ${CROSSING_STORM_SKY_STOPS.map(
  ([color, at]) => `${color} calc(50% + ${((at - 0.5) * STORM_GRADIENT_LINE_PX).toFixed(1)}px)`,
).join(', ')})`;

// The width of one sky panel, in stage px: the viewport's width, since the stage is scaled by --breath-stage-scale (set on breath-stage).
export const SKY_PANEL_W = 'calc(100vw / var(--breath-stage-scale))';

// A storm cloud is a flat base with three puffs (v28 prototype).
export const CLOUD_SHAPES = [
  { left: 0, right: 0, bottom: 0, height: '50%', borderRadius: '40px 40px 24px 24px' },
  { left: '10%', bottom: '28%', width: '34%', height: '60%', borderRadius: '50%' },
  { left: '36%', bottom: '34%', width: '40%', height: '66%', borderRadius: '50%' },
  { left: '66%', bottom: '24%', width: '30%', height: '54%', borderRadius: '50%' },
];

// The settled storm layer's four clouds, placed in the layer's content box (left/top in %, size in px).
export const STORM_LAYER_CLOUDS = [
  { left: '80%', top: '44%', width: 230, height: 78, fill: CROSSING_STORM_CLOUD_FILL.dark, opacity: 1 },
  { left: '60%', top: '30%', width: 260, height: 84, fill: CROSSING_STORM_CLOUD_FILL.mid, opacity: 1 },
  { left: '37%', top: '54%', width: 200, height: 62, fill: CROSSING_STORM_CLOUD_FILL.light, opacity: 0.85 },
  { left: '23%', top: '40%', width: 170, height: 50, fill: CROSSING_STORM_CLOUD_FILL.pale, opacity: 0.55 },
];

// The sliding storm's own three clouds, placed in its full-width box.
export const SLIDE_CLOUDS = [
  { left: '8%', top: '24%', width: 260, height: 84, fill: CROSSING_STORM_CLOUD_FILL.mid, opacity: 1 },
  { left: '40%', top: '48%', width: 200, height: 62, fill: CROSSING_STORM_CLOUD_FILL.light, opacity: 0.85 },
  { left: '58%', top: '32%', width: 230, height: 78, fill: CROSSING_STORM_CLOUD_FILL.dark, opacity: 1 },
];

export const pvRainVeil = keyframes`
  from { background-position: 0 0; }
  to { background-position: 0 34px; }
`;

// The keyframes object is interpolated, not .name, so emotion injects its CSS when the class is built.
export const motion = (frames: Keyframes, reducedMotion: boolean) => (reducedMotion ? 'none' : `${frames}`);
